import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Avatar } from '#/shared/ui/avatar'
import { Button } from '#/shared/ui/button'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'

export const USER_DROPDOWN_VARIANTS = {
  DEFAULT: 'default',
  TEAMS: 'teams',
} as const
export type UserDropdownVariant =
  (typeof USER_DROPDOWN_VARIANTS)[keyof typeof USER_DROPDOWN_VARIANTS]

export interface UserOption {
  id: string
  name: string
  avatarUrl?: string
  team?: string
  count?: number
}

export interface UserDropdownProps {
  currentUser?: UserOption
  users: UserOption[]
  onSelect?: (user: UserOption) => void
  align?: 'left' | 'center' | 'right'
  side?: 'top' | 'bottom' | 'auto'
  defaultOpen?: boolean
  /**
   * When provided, the "Reassign to" search becomes server-driven — the
   * parent owns filtering (e.g. re-fetching a teams endpoint with `q`) and
   * the dropdown skips its internal client-side filter.
   */
  onSearchChange?: (value: string) => void
  /**
   * When provided, renders a "Clear owner" button above the search (default
   * variant), or makes the "All Users" row clear-able (teams variant). Used
   * by list toolbars that drive a `user_id` filter.
   */
  onClear?: () => void
  /**
   * Panel layout. `'default'` is the reassignment UI with search + clear;
   * `'teams'` is a compact team-counts picker used by the leads page.
   */
  variant?: UserDropdownVariant
  /**
   * Teams-variant only. Drives the highlighted row: `null`/undefined
   * highlights "All Users"; a matching id highlights that user or the "me"
   * row when it matches `meUser.id`.
   */
  selectedUserId?: string | null
  /**
   * Teams-variant only. The authenticated caller shown in the top "Me" row.
   * Falls back to `currentUser` so stories/usages without a separate "me"
   * still render something sensible.
   */
  meUser?: UserOption
  /**
   * Teams-variant only. Authoritative "All Users" count from the API (which
   * includes the caller). Falls back to summing the listed users' counts —
   * but that excludes the separate `me` row, so prefer passing this.
   */
  allUsersCount?: number
  /**
   * Teams-variant only. When `false`, the count column is hidden entirely —
   * used when the caller lacks the stage's view permission and the API
   * returns null counts (which must not be shown as `0`). Defaults to `true`.
   */
  showCounts?: boolean
}

function UserDropdownDefaultPanel({
  currentUser,
  users,
  onSelect,
  onSearchChange,
  onClear,
}: UserDropdownProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const isServerSearch = onSearchChange != null

  const handleSearchChange = (value: string) => {
    setSearch(value)
    onSearchChange?.(value)
  }

  const filtered = useMemo(() => {
    if (isServerSearch) return users
    if (!search) return users
    const lower = search.toLowerCase()
    return users.filter((u) => u.name.toLowerCase().includes(lower))
  }, [users, search, isServerSearch])

  const grouped = useMemo(() => {
    const hasTeams = filtered.some((u) => u.team)
    if (!hasTeams) return [{ team: '', users: filtered }]
    const byTeam = new Map<string, UserOption[]>()
    for (const user of filtered) {
      const key = user.team ?? ''
      const bucket = byTeam.get(key)
      if (bucket) bucket.push(user)
      else byTeam.set(key, [user])
    }
    return Array.from(byTeam, ([team, teamUsers]) => ({
      team,
      users: teamUsers,
    }))
  }, [filtered])

  return (
    <div className="flex min-w-52 flex-col gap-4 rounded-md bg-white p-1 shadow-popover">
      <div className="flex flex-col gap-0.5">
        <p className="px-0.5 text-xs text-gray-500">
          {t('userDropdown.currentOwner')}
        </p>
        <div className="flex h-8 items-center rounded-sm bg-warm-50 px-2.5">
          <span className="text-sm font-medium text-gray-900">
            {currentUser?.name}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <p className="px-0.5 text-xs text-gray-500">
          {t('userDropdown.reassignTo')}
        </p>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="flex h-8 cursor-pointer items-center rounded-sm px-2.5 text-sm text-gray-500 hover:bg-gray-100"
          >
            {t('userDropdown.clearOwner')}
          </button>
        )}
        <div className="flex items-center gap-1 border-b border-gray-200 px-2.5">
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('userDropdown.typeAName')}
            className="h-8 w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
          />
          {search && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => handleSearchChange('')}
              className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-300 transition-colors hover:bg-blue-500"
            >
              <svg
                viewBox="0 0 12 12"
                fill="none"
                className="size-2 text-white"
              >
                <path
                  d="M2 2L10 10M10 2L2 10"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
        {filtered.length === 0 ? (
          <div className="flex h-8 items-center px-2.5 text-sm text-gray-500">
            {t('userDropdown.noResults')}
          </div>
        ) : (
          grouped.map((group) => (
            <div
              key={group.team || 'default'}
              className="flex flex-col gap-0.5"
            >
              {group.team && (
                <p className="px-2.5 pt-1 text-2xs font-medium tracking-wide text-gray-500 uppercase">
                  {group.team}
                </p>
              )}
              {group.users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => onSelect?.(user)}
                  className="flex h-8 cursor-pointer items-center justify-between gap-2 rounded-sm px-2.5 text-sm text-gray-900 hover:bg-gray-100"
                >
                  <span className="truncate">{user.name}</span>
                  {user.count != null && (
                    <span className="shrink-0 text-xs text-gray-500">
                      {user.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function formatCount(n: number | undefined) {
  return (n ?? 0).toLocaleString()
}

function UserDropdownTeamsPanel({
  currentUser,
  meUser,
  users,
  selectedUserId,
  allUsersCount,
  showCounts = true,
  onSelect,
  onClear,
}: Pick<
  UserDropdownProps,
  | 'currentUser'
  | 'meUser'
  | 'users'
  | 'selectedUserId'
  | 'allUsersCount'
  | 'showCounts'
  | 'onSelect'
  | 'onClear'
>) {
  const { t } = useTranslation()

  const grouped = useMemo(() => {
    const byTeam = new Map<string, UserOption[]>()
    for (const user of users) {
      const key = user.team ?? ''
      const bucket = byTeam.get(key)
      if (bucket) bucket.push(user)
      else byTeam.set(key, [user])
    }
    return Array.from(byTeam, ([team, teamUsers]) => ({
      team,
      users: teamUsers,
    }))
  }, [users])

  const summedCount = useMemo(
    () => users.reduce((sum, u) => sum + (u.count ?? 0), 0),
    [users],
  )
  const totalCount = allUsersCount ?? summedCount

  const selfUser = meUser ?? currentUser
  const selectedId = selectedUserId ?? null
  const isAllSelected = selectedId == null
  const isMeSelected = selectedId === selfUser?.id

  const rowClass = (active: boolean) =>
    cn(
      'flex h-8 w-full cursor-pointer items-center justify-between rounded-sm px-2.5 text-sm text-gray-900 hover:bg-gray-100',
      active && 'bg-warm-50 font-medium hover:bg-warm-50',
    )

  return (
    <div className="flex min-w-52 flex-col gap-1.25 rounded-md border border-gray-200 bg-white p-1 shadow-popover">
      {selfUser && (
        <>
          <button
            type="button"
            onClick={() => onSelect?.(selfUser)}
            className={rowClass(isMeSelected)}
          >
            <span className="truncate">
              {t('userDropdown.meLabel', { name: selfUser.name })}
            </span>
            {showCounts && selfUser.count != null && (
              <span className="shrink-0">{formatCount(selfUser.count)}</span>
            )}
          </button>

          <div className="h-px w-full border-t border-gray-200" />
        </>
      )}

      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={onClear}
          className={rowClass(isAllSelected)}
        >
          <span>{t('userDropdown.allUsers')}</span>
          {showCounts && <span>{formatCount(totalCount)}</span>}
        </button>

        {grouped.map((group) => (
          <div
            key={group.team || 'default'}
            className="flex flex-col gap-0.5 pt-2.5"
          >
            {group.team && (
              <p className="px-0.5 text-sm text-gray-500">{group.team}</p>
            )}
            {group.users.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => onSelect?.(user)}
                className={rowClass(selectedId === user.id)}
              >
                <span className="truncate">{user.name}</span>
                {showCounts && user.count != null && (
                  <span className="shrink-0">{formatCount(user.count)}</span>
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function resolveTriggerUser(
  isTeams: boolean,
  selectedUserId: string | null | undefined,
  users: UserOption[],
  meUser: UserOption | undefined,
  currentUser: UserOption | undefined,
): UserOption | undefined {
  if (!isTeams) return currentUser
  if (selectedUserId == null) return undefined
  if (meUser?.id === selectedUserId) return meUser
  return users.find((user) => user.id === selectedUserId) ?? currentUser
}

export function UserDropdown({
  currentUser,
  meUser,
  users,
  onSelect,
  align,
  side,
  defaultOpen,
  onSearchChange,
  onClear,
  variant = USER_DROPDOWN_VARIANTS.DEFAULT,
  selectedUserId,
  allUsersCount,
  showCounts,
}: UserDropdownProps) {
  const { t } = useTranslation()
  const isTeams = variant === USER_DROPDOWN_VARIANTS.TEAMS
  const triggerUser = resolveTriggerUser(
    isTeams,
    selectedUserId,
    users,
    meUser,
    currentUser,
  )
  const triggerLabel = triggerUser
    ? triggerUser.name
    : t('userDropdown.allUsers')
  const triggerAvatarSrc = triggerUser?.avatarUrl
  return (
    <Popover
      align={align}
      side={side}
      defaultOpen={defaultOpen}
      trigger={(open) => (
        <Button
          variant="outline"
          size="xs"
          className={cn(
            'gap-2.5 px-2.5 focus:outline-none',
            open && 'ring-2 ring-primary ring-offset-2',
          )}
        >
          <Avatar size="xs" name={triggerLabel} src={triggerAvatarSrc} />
          {triggerLabel}
          <ChevronDownIcon
            className={cn(
              'size-3 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </Button>
      )}
    >
      {(close) => {
        const closeAfter =
          <T,>(fn: ((value: T) => void) | undefined) =>
          (value: T) => {
            fn?.(value)
            close()
          }
        const handleSelect = onSelect ? closeAfter(onSelect) : undefined
        const handleClear = onClear
          ? () => {
              onClear()
              close()
            }
          : undefined
        return variant === USER_DROPDOWN_VARIANTS.TEAMS ? (
          <UserDropdownTeamsPanel
            currentUser={currentUser}
            meUser={meUser}
            users={users}
            selectedUserId={selectedUserId}
            allUsersCount={allUsersCount}
            showCounts={showCounts}
            onSelect={handleSelect}
            onClear={handleClear}
          />
        ) : (
          <UserDropdownDefaultPanel
            currentUser={currentUser}
            users={users}
            onSelect={handleSelect}
            onSearchChange={onSearchChange}
            onClear={handleClear}
          />
        )
      }}
    </Popover>
  )
}
